import LoadingComponent from 'Components/HelperComponents/LoadingComponent'
import { ArtistHasRepresentantDialog } from 'NewVersion/components/Proposal/ArtistHasRepresentantDialog'
import { EntitySelector } from 'NewVersion/components/Proposal/EntitySelector'
import { RoleSelector } from 'NewVersion/components/Proposal/RoleSelector'
import 'NewVersion/styles/ProposalsStyles.scss'
import * as path from 'Routes/Paths'
import { useEdition } from 'hooks/useEdition'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { ENS_TYPES_OBJ } from 'utils/entityUtils'

// Steps for the "Gestio" flow
const GESTIO_STEPS = {
  ROLE_SELECTION: 'role',
  ENTITY_SELECTION: 'entity',
}

/**
 * ProposalsGestioPage - Flow for "Gestió de propostes artístiques"
 *
 * Flow:
 * 1. Role Selection (Artist / Representant)
 * 2. Entity Selection (select or create artist/representant)
 * 3. -> Goes to Dashboard (Artist or Representant)
 *
 * Special case: If artist has a representant, show dialog and redirect to Representant Dashboard
 */
const ProposalsGestioPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentEdition, loading: loadingCurrentEdition } = useEdition()

  const [step, setStep] = useState(GESTIO_STEPS.ROLE_SELECTION)
  const [selectedRole, setSelectedRole] = useState(null) // 'artista' or 'representant'
  const [showRepresentantDialog, setShowRepresentantDialog] = useState(false)
  const [artistWithRepresentant, setArtistWithRepresentant] = useState(null)

  if (loadingCurrentEdition || !currentEdition) {
    return <LoadingComponent />
  }

  const handleRoleSelected = (role) => {
    setSelectedRole(role)
    setStep(GESTIO_STEPS.ENTITY_SELECTION)
  }

  const handleEntitySelected = (entity) => {
    // Handle "create new" request
    if (entity.__createNew) {
      if (selectedRole === ENS_TYPES_OBJ.artista.text) {
        // Go to create artist form
        navigate(`${path.PROPOSALS_FORM_PATH}?type=${ENS_TYPES_OBJ.artista.text}&from=gestio`)
      } else {
        // Go to create representant form
        navigate(`${path.PROPOSALS_FORM_PATH}?type=${ENS_TYPES_OBJ.representant.text}&from=gestio`)
      }
      return
    }

    if (selectedRole === ENS_TYPES_OBJ.artista.text && entity.representant) {
      // Artist has a representant - show dialog
      setArtistWithRepresentant(entity)
      setShowRepresentantDialog(true)
    } else if (selectedRole === ENS_TYPES_OBJ.artista.text) {
      // Artist without representant - go to artist dashboard
      navigateToArtistDashboard(entity)
    } else {
      // Representant - go to representant dashboard
      navigateToRepresentantDashboard(entity)
    }
  }

  const navigateToArtistDashboard = (artist) => {
    const artistId = artist['@id'] || artist.path
    const cleanId = artistId.replace(/^\//, '').split('/').pop()
    navigate(path.PROPOSALS_DASHBOARD_ARTIST_PATH.replace(':artistId', cleanId))
  }

  const navigateToRepresentantDashboard = (representant) => {
    const repId = representant['@id'] || representant.path
    const cleanId = repId.replace(/^\//, '').split('/').pop()
    navigate(path.PROPOSALS_DASHBOARD_REP_PATH.replace(':repId', cleanId))
  }

  const handleContinueAsRepresentant = () => {
    // Get the representant and navigate to their dashboard
    if (artistWithRepresentant && artistWithRepresentant.representant) {
      // representant field contains the path like "/2025/rep_id"
      const repPath = artistWithRepresentant.representant
      const cleanId = repPath.replace(/^\//, '').split('/').pop()
      navigate(path.PROPOSALS_DASHBOARD_REP_PATH.replace(':repId', cleanId))
    }
    setShowRepresentantDialog(false)
  }

  const handleBack = () => {
    if (step === GESTIO_STEPS.ENTITY_SELECTION) {
      setStep(GESTIO_STEPS.ROLE_SELECTION)
      setSelectedRole(null)
    }
  }

  return (
    <section className="proposals page-wrapper">
      <h1>{t('propostes_artistiques')}</h1>

      {step === GESTIO_STEPS.ROLE_SELECTION && <RoleSelector onRoleSelected={handleRoleSelected} />}

      {step === GESTIO_STEPS.ENTITY_SELECTION && (
        <EntitySelector
          role={selectedRole}
          onEntitySelected={handleEntitySelected}
          onBack={handleBack}
        />
      )}

      {showRepresentantDialog && (
        <ArtistHasRepresentantDialog
          artist={artistWithRepresentant}
          onContinueAsRepresentant={handleContinueAsRepresentant}
          onClose={() => setShowRepresentantDialog(false)}
        />
      )}
    </section>
  )
}

export default ProposalsGestioPage
